import React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { formatDate } from '../../utils/dateUtils';
import { MODAL_BORDER_RADIUS } from '../../utils/dateUtils';

/**
 * Modal for selecting between multiple items on a date
 * 
 * @param {Object} props
 * @param {boolean} props.open - Whether modal is open
 * @param {Function} props.onClose - Handler for closing modal
 * @param {Function} props.onItemClick - Handler for clicking an item
 * @param {Function} props.onAddNewItem - Handler for adding a new item
 * @param {Object} props.multiItemSelectState - State for multi-item selection
 * @param {Object} props.contentTypeColors - Map of content types to colors
 * @param {boolean} props.activeModal - Whether this is the active modal
 * @returns {JSX.Element}
 */

const MultiItemSelectModal = ({
    open,
    onClose,
    onItemClick,
    onAddNewItem,
    multiItemSelectState,
    contentTypeColors,
    activeModal
}) => {
    const { items, date, readOnly } = multiItemSelectState;

    return (
        <Dialog
            open={open && activeModal !== 'course' && activeModal !== 'simulation'}
            onClose={onClose}
            maxWidth="xs"
            fullWidth
            PaperProps={{
                style: { borderRadius: MODAL_BORDER_RADIUS },
                sx: {
                    bgcolor: 'background.default'
                }
            }}
            keepMounted
        >
            <DialogTitle>
                Select Item to Edit
                <Typography variant="subtitle2" color="textSecondary">
                    {date ? formatDate(date) : ''}
                </Typography>
            </DialogTitle>
            <DialogContent>
                <Box sx={{ my: 1 }}>
                    {items.map(item => (
                        <Button
                            key={item.id}
                            fullWidth
                            variant="outlined"
                            sx={{
                                mb: 1,
                                borderLeft: `6px solid ${contentTypeColors[item.contentType] || '#999'}`,
                                justifyContent: 'flex-start',
                                textAlign: 'left',
                                borderRadius: '8px',
                                opacity: readOnly ? 0.8 : 1,
                                bgcolor: 'white',
                                '&:hover': {
                                    bgcolor: 'white'
                                }
                            }}
                            onClick={() => onItemClick(item)}
                        >
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                                <Typography variant="h6" sx={{ textTransform: 'none' }}>{item.contentId}</Typography>
                                <Typography variant="caption" color="textSecondary">
                                    {item.contentType}
                                    {readOnly && " (View only - past date)"}
                                </Typography>
                            </Box>
                        </Button>
                    ))}
                </Box>
            </DialogContent>
            <DialogActions>
                {!readOnly && (
                    <Button
                        onClick={onAddNewItem}
                        color="primary"
                        variant="contained"
                        startIcon={<AddIcon />}
                    >
                        Add New Item
                    </Button>
                )}
                <Button
                    onClick={onClose}
                    variant="contained"
                    color="warning"
                >
                    {readOnly ? 'Close' : 'Cancel'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default MultiItemSelectModal;